<template>
  <div
    v-if="getUserPermission('ASK_AI')"
    class="udara-chat-widget"
    :class="[chatPosition === 'floating' ? 'floating-mode' : 'docked-mode', { 'is-open': isBotOpen }]"
  >
    <!-- Floating Action Button (floating mode only, when closed) -->
    <button
      v-if="chatPosition === 'floating' && !isBotOpen"
      class="floating-fab"
      @click="isBotOpen = true"
    >
      <feather-icon
        icon="MessageCircleIcon"
        size="22"
      />
      <span class="fab-status-dot" />
    </button>

    <!-- Chat Container -->
    <div
      v-show="chatPosition === 'docked' || isBotOpen"
      class="chat-container"
    >
      <!-- Header Bar -->
      <div
        class="chat-widget-header"
        @click="isBotOpen = !isBotOpen"
      >
        <div class="header-left">
          <div class="ai-avatar">
            <feather-icon
              icon="MessageCircleIcon"
              size="16"
            />
          </div>
          <span class="header-title">Udara AI</span>
          <span
            v-if="!isBotOpen"
            class="status-dot"
          />
        </div>
        <div class="header-actions">
          <button
            class="header-btn"
            @click.stop="isBotOpen = !isBotOpen"
          >
            <feather-icon
              :icon="chatPosition === 'floating' ? 'XIcon' : (isBotOpen ? 'ChevronDownIcon' : 'ChevronUpIcon')"
              size="18"
            />
          </button>
        </div>
      </div>

      <!-- Expandable Chat Panel -->
      <div class="chat-widget-panel">
        <!-- Messages Area -->
        <div class="chat-messages-area">
          <div
            ref="messagesInner"
            class="messages-container"
          >
            <!-- Welcome Message (when no messages) -->
            <div
              v-if="!messages.length"
              class="welcome-section"
            >
              <div class="welcome-icon">
                <feather-icon
                  icon="ZapIcon"
                  size="32"
                />
              </div>
              <h3 class="welcome-greeting">
                {{ `${ $t('Hi') }, ${ userData.firstname }` }}
              </h3>
              <p class="welcome-subtitle">
                {{ $t('How can I help you today?') }}
              </p>

              <!-- Suggestion Chips -->
              <div class="suggestion-chips">
                <button
                  v-for="(suggestion, idx) in randomSuggestions"
                  :key="idx"
                  class="suggestion-chip"
                  @click="useSuggestion(suggestion)"
                >
                  {{ suggestion }}
                </button>
              </div>
            </div>

            <!-- Chat Messages -->
            <div
              v-for="(message, index) in messages"
              :key="index"
              class="message-bubble"
              :class="message.role"
            >
              <span class="message-role">{{ message.role === 'bot' ? 'Udara AI' : $t('You') }}</span>
              <p
                class="message-text"
                v-html="message.question"
              />
              <!-- Sources Section (for bot messages) -->
              <div
                v-if="message.role === 'bot' && message.sources && message.sources.length"
                class="sources-section"
              >
                <div
                  class="sources-header"
                  @click="toggleSources(index)"
                >
                  <div class="sources-header-left">
                    <feather-icon
                      icon="FileTextIcon"
                      size="12"
                    />
                    <span>{{ $t('Sources') }}</span>
                  </div>
                  <feather-icon
                    :icon="expandedSources[index] ? 'ChevronUpIcon' : 'ChevronDownIcon'"
                    size="14"
                    class="sources-toggle-icon"
                  />
                </div>
                <ul
                  v-show="expandedSources[index]"
                  class="sources-list"
                >
                  <li
                    v-for="(source, sIdx) in getUniqueSources(message.sources)"
                    :key="sIdx"
                  >
                    <a
                      :href="source"
                      target="_blank"
                      rel="noopener noreferrer"
                    >{{ formatSourceName(source) }}</a>
                  </li>
                </ul>
              </div>
            <!-- AI Disclaimer removed from here - now at bottom -->
            </div>

            <!-- Loading Indicator -->
            <div
              v-if="isLoading"
              class="message-bubble bot"
            >
              <span class="message-role">Udara AI</span>
              <p class="message-text typing-indicator">
                <span /><span /><span />
                <em class="thinking-text">{{ $t('Thinking...') }}</em>
              </p>
            </div>
          </div>
        </div>

        <!-- Input Area -->
        <div class="chat-input-area">
          <div class="input-wrapper">
            <textarea
              ref="inputField"
              v-model="userInput"
              class="chat-input"
              :placeholder="$t('Ask Udara AI anything...')"
              rows="1"
              @keydown.enter.exact.prevent="handleMessage"
              @input="autoResizeTextarea"
            />
            <button
              class="send-btn"
              :disabled="!userInput.trim()"
              @click="handleMessage"
            >
              <feather-icon
                icon="SendIcon"
                size="18"
              />
            </button>
          </div>
          <!-- AI Disclaimer -->
          <div class="ai-disclaimer">
            <feather-icon
              icon="AlertCircleIcon"
              size="12"
            />
            <span>{{ $t('Udara AI can make mistakes. Verify responses for accuracy.') }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { $themeConfig } from '@themeConfig'
import { permissionMixin } from '@core/mixins/permission'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { mapGetters, mapActions } from 'vuex'
import useAppCustomizer from '../app-customizer/useAppCustomizer'

export default {
  name: 'AIChatBot',
  components: {},
  mixins: [sweetAlert, permissionMixin],
  data() {
    return {
      chatPosition: localStorage.getItem('chatPosition') || 'floating',
      chatPositionHandler: null,
      userInput: '',
      messages: [],
      isLoading: false,
      suggestionQuestions: [
        'What is Udara?',
        'What financial metrics are displayed on the dashboard?',
        'What is the NPL ratio and why is it important?',
        'How can I initiate End of Year Closure on Udara360?',
        'How does the Fraud Monitor work?',
      ],
      randomSuggestions: [],
      expandedSources: {}, // Track expanded state per message index
      sessionId: null,
      lastActivityTime: null,
      SESSION_TIMEOUT: 14 * 60 * 1000, // 14 minutes (slightly less than BE's 15 min to be safe)
      perfectScrollbarSettings: {
        maxScrollbarLength: 60,
        wheelPropagation: false,
      },
      showCustomizer: $themeConfig.layout.customizer,
    }
  },
  setup() {
    const {
      // Vertical Menu
      isVerticalMenuCollapsed,

      // Customizer
      isBotOpen,

      // Skin
      skin,
      skinOptions,

      // Content Width
      contentWidth,
      contentWidthOptions,

      // RTL
      isRTL,

      // routerTransition
      routerTransition,
      routerTransitionOptions,

      // Layout Type
      layoutType,
      layoutTypeOptions,

      // NavMenu Hidden
      isNavMenuHidden,

      // Navbar
      navbarColors,
      navbarTypes,
      navbarBackgroundColor,
      navbarType,

      // Footer
      footerTypes,
      footerType,
    } = useAppCustomizer()

    if (layoutType.value === 'horizontal') {
      // Remove semi-dark skin option in horizontal layout
      const skinSemiDarkIndex = skinOptions.value.findIndex(s => s.value === 'semi-dark')
      delete skinOptions.value[skinSemiDarkIndex]

      // Remove menu hidden radio in horizontal layout => As we already have switch for it
      const menuHiddneIndex = navbarTypes.findIndex(t => t.value === 'hidden')
      delete navbarTypes[menuHiddneIndex]
    }
    return {
      // Vertical Menu
      isVerticalMenuCollapsed,

      // Customizer
      isBotOpen,

      // Skin
      skin,
      skinOptions,

      // Content Width
      contentWidth,
      contentWidthOptions,

      // RTL
      isRTL,

      // routerTransition
      routerTransition,
      routerTransitionOptions,

      // Layout Type
      layoutType,
      layoutTypeOptions,

      // NavMenu Hidden
      isNavMenuHidden,

      // Navbar
      navbarColors,
      navbarTypes,
      navbarBackgroundColor,
      navbarType,

      // Footer
      footerTypes,
      footerType,
    }
  },
  computed: {
    ...mapGetters({
      userData: 'auth/getCurrentUser',
    }),
  },
  watch: {
    messages() {
      this.$nextTick(() => {
        const el = this.$refs.messagesInner
        if (el) {
          el.scrollTo({
            top: el.scrollHeight,
            behavior: 'smooth',
          })
        }
      })
    },
  },
  created() {
    this.shuffleSuggestions()
    this.chatPositionHandler = e => {
      this.chatPosition = e.detail
    }
    window.addEventListener('chatPositionChanged', this.chatPositionHandler)
  },
  beforeDestroy() {
    window.removeEventListener('chatPositionChanged', this.chatPositionHandler)
  },
  methods: {
    ...mapActions({
      createQuestions: 'account/createQuestionsAction',
    }),

    shuffleSuggestions() {
      const shuffled = [...this.suggestionQuestions].sort(() => Math.random() - 0.5)
      this.randomSuggestions = shuffled.slice(0, Math.min(4, shuffled.length))
    },

    useSuggestion(suggestion) {
      this.userInput = suggestion
      this.handleMessage()
    },

    async handleMessage() {
      const question = this.userInput.trim()
      if (!question) {
        return
      }

      try {
        this.isLoading = true
        this.messages.push({ role: 'user', question })

        // Clear input immediately after sending
        this.userInput = ''
        this.resetTextareaHeight()

        // Check if session has expired (14 min idle timeout) - BEFORE building formData
        const sessionExpired = this.isSessionExpired()
        if (sessionExpired) {
          this.clearSession()
        }

        const formData = {
          question,
        }

        // Include sessionId ONLY if we have a valid (non-expired) one
        if (this.sessionId && !sessionExpired) {
          formData.sessionId = this.sessionId
        }

        // Update activity time BEFORE making the request
        if (this.sessionId) {
          this.lastActivityTime = Date.now()
        }

        const response = await this.createQuestions({ formData, permission: 'ASK_AI' })

        const { status, data } = await response
        if (status === 200 && data) {
          const parsed = this.parseAnswer(data.answer)

          // Store sessionId from response for subsequent requests
          if (data.sessionId) {
            this.sessionId = data.sessionId
            this.lastActivityTime = Date.now()
          }

          this.messages.push({
            role: 'bot',
            question: parsed,
            sources: data.sourceDocuments || [],
            provider: data.provider || null,
          })
          this.isLoading = false
        }
      } catch (error) {
        // Check if this is a session-related error from backend
        if (this.isSessionError(error)) {
          this.clearSession()
          // Show user-friendly message
          this.messages.push({
            role: 'bot',
            question: 'Your session has expired. Please try sending your message again.',
            sources: [],
            provider: null,
          })
        } else {
          // Restore input on failure so user can retry
          this.userInput = question
          this.handleError(error)
        }
      } finally {
        this.isLoading = false
      }
    },

    isSessionExpired() {
      if (!this.sessionId || !this.lastActivityTime) {
        return false
      }
      const elapsed = Date.now() - this.lastActivityTime
      const expired = elapsed > this.SESSION_TIMEOUT
      // if (expired) {
      //   console.log(`[AIChatBot] Session timeout check: elapsed=${Math.round(elapsed / 1000)}s, timeout=${this.SESSION_TIMEOUT / 1000}s, expired=${expired}`)
      // }
      return expired
    },

    isSessionError(error) {
      // Check for common session expiry error patterns from backend
      const errorMsg = error?.response?.data?.message || error?.message || ''
      const errorStatus = error?.response?.status

      // Check for specific error messages or status codes indicating session expiry
      const sessionErrorPatterns = [
        'session',
        'expired',
        'invalid session',
        'session not found',
        'session timeout',
      ]

      const isSessionRelated = sessionErrorPatterns.some(
        pattern => errorMsg.toLowerCase().includes(pattern),
      )

      // Also treat 401/403 as potential session errors
      const isAuthError = errorStatus === 401 || errorStatus === 403

      return isSessionRelated || isAuthError
    },

    clearSession() {
      this.sessionId = null
      this.lastActivityTime = null
    },

    autoResizeTextarea(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`
    },

    resetTextareaHeight() {
      this.$nextTick(() => {
        const textarea = this.$refs.inputField
        if (textarea) {
          textarea.style.height = 'auto'
        }
      })
    },

    parseAnswer(rawAnswer) {
      const responseIndex = rawAnswer.indexOf('Response:')
      const cleaned = responseIndex === -1
        ? rawAnswer
        : rawAnswer.slice(responseIndex + 'Response:'.length).trim()

      return cleaned
      // Bold: **text** → <strong>text</strong>
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      // Numbered list items onto their own line
        .replace(/(\d+\.)/g, '<br><br>$1')
      // Sub-bullets (letter or dash preceded by spaces)
        .replace(/\n\s+[-–]\s+/g, '<br>&nbsp;&nbsp;• ')
      // Remaining newlines
        .replace(/\n/g, '<br>')
      // Clean up more than 2 consecutive breaks
        .replace(/(<br>){3,}/g, '<br><br>')
        .trim()
    },

    getUniqueSources(sources) {
      return [...new Set(sources)]
    },

    formatSourceName(source) {
      // Extract filename from S3 URL or full path
      const parts = source.split('/')
      return parts[parts.length - 1] || source
    },

    toggleSources(index) {
      this.$set(this.expandedSources, index, !this.expandedSources[index])
    },
  },
}
</script>

<style lang="scss">
@import '@core/scss/vue/libs/vue-select.scss';
</style>

<style lang="scss" scoped>
@import '~@core/scss/base/bootstrap-extended/include';
@import '~@core/scss/base/components/variables-dark';

// Chat Widget Base
.udara-chat-widget {
  position: fixed;
  z-index: 1031;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, sans-serif;
}

// ========================
// DOCKED MODE (LinkedIn-style)
// ========================
.docked-mode {
  bottom: 0;
  right: 24px;
  width: 340px;
  border-radius: 8px 8px 0 0;
  overflow: hidden;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.16);
  transition: box-shadow 0.2s ease;

  .dark-layout & {
    box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.1), 0 4px 12px rgba(0, 0, 0, 0.4);
  }

  &:hover {
    box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.1), 0 8px 24px rgba(0, 0, 0, 0.2);
  }

  .chat-widget-panel {
    height: 0;
    overflow: hidden;
    transition: height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &.is-open .chat-widget-panel {
    height: 550px;
  }
}

// ========================
// FLOATING MODE (Bubble)
// ========================
.floating-mode {
  bottom: 24px;
  right: 24px;

  .chat-container {
    position: absolute;
    bottom: 0;
    right: 0;
    width: 380px;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2), 0 0 0 1px rgba(0, 0, 0, 0.08);
    animation: float-panel-in 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
    transform-origin: bottom right;

    .dark-layout & {
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.1);
    }
  }

  .chat-widget-panel {
    height: 500px;
    overflow: hidden;
  }
}

@keyframes float-panel-in {
  from {
    opacity: 0;
    transform: scale(0.9) translateY(8px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

// Floating Action Button
.floating-fab {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  border: none;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.24);
  transition: all 0.2s ease;
  position: relative;

  .dark-layout & {
    background: linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%);
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  }

  &:hover {
    transform: scale(1.08);
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.32);
  }

  &:active {
    transform: scale(0.95);
  }
}

.fab-status-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #31a24c;
  border: 2px solid #1a1a2e;
  animation: pulse-green 2s infinite;

  .dark-layout & {
    border-color: #0d1b2a;
  }
}

// Header Bar (LinkedIn-style)
.chat-widget-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  cursor: pointer;
  transition: background 0.2s ease;

  .dark-layout & {
    background: linear-gradient(135deg, #0d1b2a 0%, #1b263b 100%);
  }

  &:hover {
    background: linear-gradient(135deg, #16213e 0%, #0f3460 100%);
  }
}

.header-left {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ai-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 85%, #000) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  flex-shrink: 0;
}

.header-title {
  font-size: 14px;
  font-weight: 600;
  color: #fff;
  letter-spacing: 0.2px;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #31a24c;
  box-shadow: 0 0 0 2px #16213e;
  animation: pulse-green 2s infinite;
}

@keyframes pulse-green {
  0%, 100% { box-shadow: 0 0 0 2px #16213e; }
  50% { box-shadow: 0 0 0 4px rgba(49, 162, 76, 0.3); }
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 4px;
}

.header-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: #fff;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
}

// Chat Panel (expandable)
.chat-widget-panel {
  background: #fff;
  display: flex;
  flex-direction: column;

  .dark-layout & {
    background: $theme-dark-card-bg;
  }
}

// Messages Area
.chat-messages-area {
  flex: 1;
  overflow: hidden;
  position: relative;
}

.messages-container {
  height: 100%;
  overflow-y: auto;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;

    .dark-layout & {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

// Welcome Section
.welcome-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px;
  height: 100%;
}

.welcome-icon {
  width: 64px;
  height: 64px;
  min-width: 64px;
  min-height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 85%, #000) 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-bottom: 16px;

  svg {
    width: 32px;
    height: 32px;
    flex-shrink: 0;
  }
}

.welcome-greeting {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a2e;
  margin-bottom: 4px;

  .dark-layout & {
    color: #fff;
  }
}

.welcome-subtitle {
  font-size: 14px;
  color: #666;
  margin: 0;

  .dark-layout & {
    color: #b9b9c3;
  }
}

// Suggestion Chips
.suggestion-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 16px;
  justify-content: center;
}

.suggestion-chip {
  padding: 8px 14px;
  border: 1px solid #e4e6eb;
  border-radius: 20px;
  background: #fff;
  font-size: 12px;
  color: #1a1a2e;
  cursor: pointer;
  transition: all 0.2s ease;
  line-height: 1.4;
  text-align: left;

  &:hover {
    background: color-mix(in srgb, var(--primary) 8%, transparent);
    border-color: var(--primary);
    color: var(--primary);
  }

  .dark-layout & {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.15);
    color: #b9b9c3;

    &:hover {
      background: color-mix(in srgb, var(--primary) 15%, transparent);
      border-color: var(--primary);
      color: var(--primary);
    }
  }
}

// Message Bubbles
.message-bubble {
  max-width: 85%;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &.user {
    align-self: flex-end;
    align-items: flex-end;

    .message-text {
      background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 85%, #000) 100%);
      color: #fff;
      border-radius: 18px 18px 4px 18px;
    }
  }

  &.bot {
    align-self: flex-start;
    align-items: flex-start;

    .message-text {
      background: #f0f2f5;
      color: #1a1a2e;
      border-radius: 18px 18px 18px 4px;

      .dark-layout & {
        background: $theme-dark-body-bg;
        color: $theme-dark-body-color;
      }
    }
  }
}

.message-role {
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #999;
  padding: 0 4px;
}

.message-text {
  margin: 0;
  padding: 10px 14px;
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
}

// Sources Section
.sources-section {
  margin-top: 8px;
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 8px;
  font-size: 12px;

  .dark-layout & {
    background: rgba(255, 255, 255, 0.05);
  }
}

.sources-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 4px 0;
  user-select: none;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
}

.sources-header-left {
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  color: #666;

  .dark-layout & {
    color: #b9b9c3;
  }
}

.sources-toggle-icon {
  color: #666;
  transition: transform 0.2s ease;

  .dark-layout & {
    color: #b9b9c3;
  }
}

.sources-list {
  margin: 0;
  margin-top: 6px;
  padding-left: 18px;
  list-style: decimal;

  li {
    margin-bottom: 2px;

    a {
      color: var(--primary);
      text-decoration: none;
      word-break: break-all;

      &:hover {
        text-decoration: underline;
      }
    }
  }
}

// AI Disclaimer
.ai-disclaimer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-top: 10px;
  padding: 6px 12px;
  background: rgba(255, 193, 7, 0.12);
  border: 1px solid rgba(255, 193, 7, 0.25);
  border-radius: 6px;
  font-size: 9px;
  color: #856404;
  line-height: 1.4;
  text-align: center;

  .dark-layout & {
    background: rgba(255, 193, 7, 0.1);
    border-color: rgba(255, 193, 7, 0.2);
    color: #ffc107;
  }

  svg {
    flex-shrink: 0;
    color: #d39e00;

    .dark-layout & {
      color: #ffc107;
    }
  }
}

// Typing Indicator
.typing-indicator {
  display: flex;
  align-items: center;
  gap: 4px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #999;
    animation: typing-bounce 1.4s infinite ease-in-out;

    &:nth-child(2) { animation-delay: 0.2s; }
    &:nth-child(3) { animation-delay: 0.4s; }
  }
}

@keyframes typing-bounce {
  0%, 80%, 100% { transform: scale(0.8); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}

.thinking-text {
  font-size: 12px;
  color: #999;
  margin-left: 8px;
  font-style: italic;
}

// Input Area
.chat-input-area {
  padding: 12px;
  border-top: 1px solid #e4e6eb;
  background: #fff;

  .dark-layout & {
    background: $theme-dark-card-bg;
    border-color: $theme-dark-border-color;
  }
}

.input-wrapper {
  position: relative;
  border: 1px solid #e4e6eb;
  border-radius: 16px;
  background: #fff;
  overflow: hidden;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 2px color-mix(in srgb, var(--primary) 20%, transparent);
  }

  .dark-layout & {
    background: $theme-dark-body-bg;
    border-color: $theme-dark-border-color;

    &:focus-within {
      border-color: var(--primary);
    }
  }
}

.chat-input {
  width: 100%;
  border: none;
  padding: 12px 14px;
  padding-bottom: 48px; // Space for the send button
  font-size: 14px;
  font-family: inherit;
  color: #1a1a2e;
  background: transparent;
  outline: none;
  resize: none;
  min-height: 80px;
  max-height: 160px;
  overflow-y: auto;
  line-height: 1.5;
  box-sizing: border-box;

  &::placeholder {
    color: #999;
  }

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 2px;
  }

  .dark-layout & {
    color: $theme-dark-body-color;

    &::-webkit-scrollbar-thumb {
      background: rgba(255, 255, 255, 0.2);
    }
  }
}

.send-btn {
  position: absolute;
  bottom: 8px;
  right: 8px;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary) 0%, color-mix(in srgb, var(--primary) 85%, #000) 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;

  &:hover:not(:disabled) {
    transform: scale(1.05);
    box-shadow: 0 4px 12px color-mix(in srgb, var(--primary) 40%, transparent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
}

// Responsive: full-width on small screens
@media (max-width: 767.98px) {
  .docked-mode {
    right: 0;
    left: 0;
    width: 100%;
    border-radius: 8px 8px 0 0;

    &.is-open .chat-widget-panel {
      height: 70vh;
    }
  }

  .floating-mode {
    bottom: 16px;
    right: 12px;

    .chat-container {
      position: fixed;
      bottom: 0;
      right: 0;
      left: 0;
      width: 100%;
      border-radius: 12px 12px 0 0;
    }

    .chat-widget-panel {
      height: 70vh;
    }
  }

  .floating-fab {
    width: 48px;
    height: 48px;
  }

  .welcome-section {
    padding: 24px 16px;
  }

  .welcome-icon {
    width: 52px;
    height: 52px;
    min-width: 52px;
    min-height: 52px;

    svg {
      width: 26px;
      height: 26px;
    }
  }

  .welcome-greeting {
    font-size: 18px;
  }

  .suggestion-chips {
    gap: 6px;
  }

  .suggestion-chip {
    font-size: 11px;
    padding: 6px 12px;
  }

  .chat-input {
    min-height: 64px;
    padding-bottom: 44px;
  }
}
</style>
